/**
 * 品牌画像「原始 LLM JSON ↔ 表单视图模型」映射层。
 *
 * 后端 v3 起删除了服务端转换层，BrandPortraitDto 直接对齐 LLM 原始结构
 * （brand / products / competitors / market 均为原始对象/数组）。
 * 字段→展示标签的映射改由前端负责，集中在本文件，schema 变更只改这里。
 */

export interface InfoItem {
  key?: string
  label: string
  value: string
}
export interface ProductItem {
  name: string
  description: string
  // 保留原始拆分字段用于保存回写（description 为 category | price_band 的组合展示）
  category?: string
  priceBand?: string
}
export interface CompetitorItem {
  brand: string
  priceBand: string
  traits: string[]
  audience: string
  vsTarget: string
}
export interface CompetitorGroup {
  key: string
  label: string
  items: CompetitorItem[]
}

export interface ScenarioItem {
  name: string
  trigger: string
  constraints: string[]
  usage: string
  decisionWindow: string
  decisionType: string
  linkedPersona: string
  personaProfile: string
  confidence: string
}

export interface PortraitView {
  brandName: string
  brandSlogan: string
  brandTags: { name: string }[]
  brandInfoList: InfoItem[]
  productTitle: string
  priceRange: string
  productItems: ProductItem[]
  competitorTitle: string
  competitorGroups: CompetitorGroup[]
  priceDisclaimer: string
  scenariosTitle: string
  scenarios: ScenarioItem[]
}

/** 品牌基础信息：原始 brand.key → 展示标签（顺序即展示顺序） */
const BRAND_FIELD_DEFS: { key: string; label: string }[] = [
  { key: 'name_cn', label: '品牌全称' },
  { key: 'legal_entity', label: '工商主体' },
  { key: 'parent', label: '集团归属' },
  { key: 'parent_chain', label: '归属链' },
  { key: 'positioning_tier', label: '定位层级' },
  { key: 'headquarters', label: '总部所在地' },
  { key: 'core_value', label: '核心价值' },
  { key: 'target', label: '目标人群' },
  { key: 'brand_stage', label: '品牌阶段' },
  { key: 'origin_story', label: '创立背景' },
]

/** 竞品分类：原始 competitors.key → 展示标签（与卡片顺序一致） */
const COMPETITOR_CATS: { key: string; label: string }[] = [
  { key: 'same_price', label: '同价位直接竞品' },
  { key: 'same_form', label: '同级形态竞品' },
  { key: 'mind_substitute', label: '心智替代竞品' },
  { key: 'price_downgrade', label: '价格下探竞品' },
  { key: 'benchmark', label: '高势能标杆竞品' },
]

function toStr(v: any): string {
  if (v == null) return ''
  if (Array.isArray(v)) return v.map(toStr).filter(Boolean).join('、')
  return String(v)
}

/** 逗号/顿号分隔的短语文本 → 标签数组，用于以 chip 形式展示 */
function toChips(v: any): string[] {
  if (Array.isArray(v)) return v.map(toStr).filter(Boolean)
  return toStr(v)
    .split(/[、,，]/)
    .map(s => s.trim())
    .filter(Boolean)
}

/** 各模块是否已就绪（驱动逐块渲染/骨架） */
export function moduleReady(raw: any) {
  return {
    brand: !!raw?.brand,
    products: Array.isArray(raw?.products) && raw.products.length > 0,
    competitors: !!raw?.competitors,
    market: !!raw?.market,
    scenarios: Array.isArray(raw?.purchase_scenarios) && raw.purchase_scenarios.length > 0,
  }
}

/** 原始 LLM 画像 → 表单视图模型 */
export function rawToView(raw: any): PortraitView {
  const brand = raw?.brand || {}
  const products = Array.isArray(raw?.products) ? raw.products : []
  const competitors = raw?.competitors || {}

  const brandInfoList: InfoItem[] = BRAND_FIELD_DEFS.filter(
    d => brand[d.key] != null && brand[d.key] !== '',
  ).map(d => ({ key: d.key, label: d.label, value: toStr(brand[d.key]) }))

  const aliases = toStr(brand.aliases)
  if (aliases) brandInfoList.push({ key: 'aliases', label: '别名/简称', value: aliases })

  // 用户自定义字段（无 key，保存时写回 brand.custom_fields）
  if (Array.isArray(brand.custom_fields)) {
    brand.custom_fields.forEach((f: any) => {
      brandInfoList.push({ label: toStr(f?.label), value: toStr(f?.value) })
    })
  }

  let tagsSource: any[] = []
  if (Array.isArray(brand.brand_tags)) tagsSource = brand.brand_tags
  else if (Array.isArray(raw?.infoSources)) tagsSource = raw.infoSources

  const productItems: ProductItem[] = products.map((p: any) => {
    const category = toStr(p?.category)
    const priceBand = toStr(p?.price_band)
    return {
      name: toStr(p?.name),
      description: [category, priceBand].filter(Boolean).join(' | '),
      category,
      priceBand,
    }
  })
/**
 * {
  "same_form": [
    {
      "audience": "所有需要搜索流量的企业",
      "traits": "搜索流量霸主，官方背书",
      "vs_target": "百度是流量方，珍岛是技术服务方，二者既有合作又有竞争",
      "name": "百度营销",
      "price_band": "按点击/投放计费"
    },
    {
      "audience": "淘宝/天猫商家",
      "traits": "电商营销基础设施",
      "vs_target": "局限于阿里生态，珍岛跨平台",
      "name": "阿里妈妈",
      "price_band": "按投放计费"
    }
  ],
  "price_downgrade": [
    {
      "audience": "微型企业、个体户",
      "traits": "仅解决展示问题，无营销深度",
      "vs_target": "功能单一，缺乏AI营销和数据沉淀能力",
      "name": "自助建站工具（如凡科、上线了）",
      "price_band": "数百至数千元/年"
    },
    {
      "audience": "极小预算初创团队",
      "traits": "非标准化，风险高",
      "vs_target": "缺乏稳定性、合规性和规模化能力",
      "name": "自由职业者/个人SEO",
      "price_band": "低廉"
    }
  ],
  "same_price": [
    {
      "audience": "零售、餐饮等注重私域流量的企业",
      "traits": "腾讯生态深耕，社交电商能力强",
      "vs_target": "微盟侧重社交/交易闭环，珍岛侧重全域搜索/内容营销",
      "name": "微盟",
      "price_band": "年均数万至数十万元"
    },
    {
      "audience": "纯电商卖家、品牌零售商",
      "traits": "电商SaaS标杆，开店工具强大",
      "vs_target": "有赞强在交易工具，珍岛强在营销获客",
      "name": "有赞",
      "price_band": "年均数万元起"
    }
  ],
  "benchmark": [
    {
      "audience": "全球大型跨国企业",
      "traits": "全球CRM/营销云标杆",
      "vs_target": "国际巨头，本土化适配不足，价格极高，珍岛更懂中国中小企业",
      "name": "Salesforce",
      "price_band": "高昂"
    },
    {
      "audience": "成长型科技企业",
      "traits": "集客营销理念倡导者",
      "vs_target": "国际SaaS典范，珍岛对其在国内市场的本土化对标",
      "name": "HubSpot",
      "price_band": "中高"
    }
  ],
  "mind_substitute": [
    {
      "audience": "大型品牌企业",
      "traits": "创意驱动，品牌策略强",
      "vs_target": "传统人工服务成本高、周期长，珍岛提供标准化SaaS替代",
      "name": "4A广告公司",
      "price_band": "项目制，数十万至百万"
    },
    {
      "audience": "缺乏运营团队的中小企业",
      "traits": "人力密集型，全托管",
      "vs_target": "代运营不可控因素多，珍岛提供工具赋能企业自建能力",
      "name": "代运营公司",
      "price_band": "服务费+提成"
    }
  ]
}
 */
  const competitorGroups: CompetitorGroup[] = COMPETITOR_CATS.map(cat => {
    const arr = Array.isArray(competitors[cat.key]) ? competitors[cat.key] : []
    return {
      key: cat.key,
      label: cat.label,
      items: arr.map((x: any) => ({
        brand: toStr(x?.name ?? x),
        priceBand: toStr(x?.price_band),
        traits: toChips(x?.traits),
        audience: toStr(x?.audience),
        vsTarget: toStr(x?.vs_target),
      })),
    }
  })

  const personasArr = Array.isArray(raw?.personas) ? raw.personas : []
  const personaMap = new Map<string, any>()
  personasArr.forEach((p: any) => {
    if (p?.role) personaMap.set(p.role, p)
  })

  const scenarios: ScenarioItem[] = (Array.isArray(raw?.purchase_scenarios) ? raw.purchase_scenarios : []).map(
    (s: any) => {
      const persona = personaMap.get(s?.linked_persona) || {}
      return {
        name: toStr(s?.name),
        trigger: toStr(s?.trigger),
        constraints: Array.isArray(s?.constraints) ? s.constraints.map(toStr).filter(Boolean) : [],
        usage: toStr(s?.usage),
        decisionWindow: toStr(s?.decision_window),
        decisionType: toStr(s?.decision_type),
        linkedPersona: toStr(s?.linked_persona || persona?.role),
        personaProfile: toStr(persona?.profile),
        confidence: toStr(s?.confidence),
      }
    },
  )

  return {
    brandName: toStr(raw?.brandName || brand.name_cn),
    brandSlogan: toStr(brand.core_value || brand.slogan),
    brandTags: tagsSource.map((t: any) => ({ name: toStr(t) })),
    brandInfoList,
    productTitle: '核心产品/服务与价值区间',
    priceRange: toStr(raw?.main_pool_price_band),
    productItems,
    competitorTitle: '竞品矩阵',
    competitorGroups,
    priceDisclaimer: toStr(raw?.priceDisclaimer),
    scenariosTitle: '场景定义',
    scenarios,
  }
}

/**
 * 表单视图模型 → 原始 LLM 画像（保存用）。
 * 以 baseRaw 为底做合并，保证未编辑的字段（含 bns_baseline 等内部模块）原样保留。
 */
export function viewToRaw(view: PortraitView, baseRaw: any): any {
  const out = baseRaw ? JSON.parse(JSON.stringify(baseRaw)) : {}
  const brand = { ...(out.brand || {}) }

  // 基础信息：按 key 回写编辑值；无 key 的用户新增字段写入 brand.custom_fields
  const customFields: { label: string; value: string }[] = []
  view.brandInfoList.forEach(item => {
    if (!item.key) {
      if (item.label || item.value) customFields.push({ label: item.label, value: item.value })
      return
    }
    if (item.key === 'aliases') {
      brand.aliases = item.value
        ? item.value
            .split(/[、,]/)
            .map(s => s.trim())
            .filter(Boolean)
        : []
    } else {
      brand[item.key] = item.value
    }
  })
  brand.custom_fields = customFields
  brand.brand_tags = view.brandTags.map(t => t.name).filter(Boolean)
  if (view.brandSlogan) brand.core_value = view.brandSlogan
  if (view.brandName) brand.name_cn = brand.name_cn || view.brandName
  out.brand = brand

  // 核心产品
  out.products = view.productItems.map(p => {
    const [category, priceBand] = p.description.includes(' | ')
      ? p.description.split(' | ')
      : [p.description, p.priceBand || '']
    return { name: p.name, category: (category || '').trim(), price_band: (priceBand || '').trim() }
  })
  out.main_pool_price_band = view.priceRange

  // 竞品（按名称匹配保留原始 reason 等字段）
  const competitors = { ...(out.competitors || {}) }
  view.competitorGroups.forEach(g => {
    const prev: any[] = Array.isArray(competitors[g.key]) ? competitors[g.key] : []
    competitors[g.key] = g.items.map(i => {
      const old = prev.find(x => (x?.name ?? x) === i.brand)
      const base = old && typeof old === 'object' ? { ...old } : {}
      return {
        ...base,
        name: i.brand,
        price_band: i.priceBand,
        traits: i.traits.join('、'),
        audience: i.audience,
        vs_target: i.vsTarget,
      }
    })
  })
  out.competitors = competitors

  // 场景定义
  out.purchase_scenarios = view.scenarios.map(s => ({
    name: s.name,
    trigger: s.trigger,
    constraints: s.constraints,
    usage: s.usage,
    decision_window: s.decisionWindow,
    decision_type: s.decisionType,
    linked_persona: s.linkedPersona,
    confidence: s.confidence,
  }))

  out.brandName = view.brandName
  return out
}
